import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Sparkles, Cloud, Video, Instagram, Database, ExternalLink,
  AlertTriangle, TrendingUp, RefreshCw, CheckCircle2,
} from "lucide-react";

const PROVIDER_ICON = {
  rapidapi_instagram: Instagram,
  gemini_ai: Sparkles,
  cloudflare_r2: Cloud,
  cloudflare_stream: Video,
};

const LEVEL_STYLE = {
  ok:       { bar: "bg-emerald-500", badge: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "جيد" },
  warn:     { bar: "bg-amber-500",   badge: "bg-amber-100 text-amber-900 border-amber-300",     label: "تحذير" },
  critical: { bar: "bg-rose-500",    badge: "bg-rose-100 text-rose-900 border-rose-300",         label: "حرج" },
};

function fmtBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = Number(n);
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 2 : 0)} ${units[i]}`;
}

function fmtUsed(p) {
  if (p.id === "cloudflare_r2") return fmtBytes(p.used);
  return `${(p.used || 0).toLocaleString("ar-EG")} ${p.unit}`;
}
function fmtQuota(p) {
  if (p.id === "cloudflare_r2") return fmtBytes(p.quota);
  return `${(p.quota || 0).toLocaleString("ar-EG")} ${p.unit}`;
}
function fmtRemaining(p) {
  if (p.id === "cloudflare_r2") return fmtBytes(p.remaining);
  return `${(p.remaining || 0).toLocaleString("ar-EG")} ${p.unit}`;
}

export default function AdminUsage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/usage");
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return <div className="text-center text-muted-foreground py-16">جاري التحميل...</div>;
  }
  if (!data) return null;

  const critical = data.providers.filter((p) => p.level === "critical");
  const warns = data.providers.filter((p) => p.level === "warn");

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-usage-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-semibold" data-testid="usage-title">
            استهلاك الخدمات
          </h1>
          <p className="text-foreground/60 text-sm mt-1">
            راقب حصص Instagram + الذكاء الاصطناعي + Cloudflare هذا الشهر — {data.month}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-2" data-testid="btn-refresh-usage">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> تحديث
        </Button>
      </div>

      {/* Alerts panel */}
      {(critical.length > 0 || warns.length > 0) && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-2" data-testid="usage-alerts">
          <div className="flex items-center gap-2 font-semibold text-amber-900">
            <AlertTriangle className="h-4 w-4" />
            {critical.length > 0 ? "تحذير عاجل — استهلاك مرتفع جداً" : "تنبيه — يقترب من الحد"}
          </div>
          <ul className="text-sm text-amber-900 space-y-1 ps-6 list-disc">
            {critical.map((p) => (
              <li key={p.id}>
                <span className="font-medium">{p.label}</span> — استهلكت {p.percent}٪ ({fmtUsed(p)} من {fmtQuota(p)}).
                <a href={p.upgrade_url} target="_blank" rel="noreferrer" className="underline ms-1">ترقية الآن</a>
              </li>
            ))}
            {warns.map((p) => (
              <li key={p.id}>
                <span className="font-medium">{p.label}</span> — {p.percent}٪ مستهلَك ({fmtUsed(p)} من {fmtQuota(p)}).
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Provider cards */}
      <div className="grid gap-4 sm:grid-cols-2">
        {data.providers.map((p) => (
          <ProviderCard key={p.id} p={p} />
        ))}
      </div>

      {/* Database size */}
      <Card className="p-5 border-foreground/10" data-testid="usage-db-card">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-black text-white grid place-items-center">
            <Database className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold">قاعدة البيانات (MongoDB)</div>
            <div className="text-xs text-foreground/60 mt-0.5">
              الحجم الحالي: {data.database.size_mb.toLocaleString("ar-EG")} م.ب ({data.database.size_gb.toLocaleString("ar-EG")} ج.ب)
            </div>
          </div>
        </div>
      </Card>

      {/* Free-tier reminder */}
      <div className="rounded-xl border border-foreground/10 bg-white p-4 text-xs text-foreground/70 space-y-1.5">
        <div className="font-semibold text-foreground flex items-center gap-1.5">
          <TrendingUp className="h-3.5 w-3.5" /> ملاحظات مهمة
        </div>
        <ul className="ps-5 list-disc space-y-1">
          <li>حصص RapidAPI و Gemini تُصفَّر أول كل شهر ميلادي — لا تقلق من التراكم.</li>
          <li>تخزين Cloudflare R2 و Stream <b>تراكمي</b> — احذف الفيديوهات/الصور القديمة إن تجاوزت الحد.</li>
          <li>سيظهر تحذير أصفر عند 70٪ وأحمر عاجل عند 90٪ — راقب التنبيهات في الأعلى.</li>
        </ul>
      </div>
    </div>
  );
}

function ProviderCard({ p }) {
  const Icon = PROVIDER_ICON[p.id] || Sparkles;
  const style = LEVEL_STYLE[p.level] || LEVEL_STYLE.ok;

  return (
    <Card className={`p-5 border-foreground/10 relative overflow-hidden`} data-testid={`usage-card-${p.id}`}>
      {p.level === "critical" && (
        <div className="absolute inset-x-0 top-0 h-1 bg-rose-500" />
      )}
      {p.level === "warn" && (
        <div className="absolute inset-x-0 top-0 h-1 bg-amber-500" />
      )}

      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-black text-white grid place-items-center shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="font-display font-semibold" data-testid={`usage-label-${p.id}`}>{p.label}</div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${style.badge}`} data-testid={`usage-level-${p.id}`}>
              {style.label} · {p.percent}٪
            </span>
          </div>
          <p className="text-xs text-foreground/60 mt-1 leading-relaxed">{p.note}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-foreground/60">
            <span className="font-semibold text-foreground">{fmtUsed(p)}</span> من {fmtQuota(p)}
          </span>
          <span className="text-foreground/50">متبقّي: {fmtRemaining(p)}</span>
        </div>
        <div className="h-2 rounded-full bg-foreground/5 overflow-hidden">
          <div
            className={`h-full ${style.bar} transition-all`}
            style={{ width: `${Math.min(100, p.percent)}%` }}
            data-testid={`usage-bar-${p.id}`}
          />
        </div>
      </div>

      {/* Event breakdown (small) */}
      {p.events && Object.keys(p.events).length > 0 && (
        <div className="mt-4 pt-3 border-t border-foreground/5">
          <div className="text-[10px] font-semibold text-foreground/50 uppercase tracking-wider mb-1.5">تفصيل الاستدعاءات</div>
          <div className="flex flex-wrap gap-1.5" data-testid={`usage-events-${p.id}`}>
            {Object.entries(p.events).map(([k, v]) => (
              <span key={k} className="text-[11px] bg-foreground/5 px-2 py-1 rounded-full">
                {EVENT_LABEL[k] || k}: <b>{typeof v === "number" ? v.toLocaleString("ar-EG") : v}</b>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Upgrade CTA — only when reaching warn/critical */}
      {(p.level === "warn" || p.level === "critical") && p.upgrade_url && (
        <div className="mt-4 pt-3 border-t border-foreground/5 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-foreground/70">
            <div className="font-semibold text-foreground mb-0.5">للترقية:</div>
            <div>{p.next_tier}</div>
          </div>
          <a href={p.upgrade_url} target="_blank" rel="noreferrer"
             className="shrink-0 inline-flex items-center gap-1 bg-black text-white text-xs px-3 py-1.5 rounded-full hover:bg-black/85"
             data-testid={`usage-upgrade-${p.id}`}>
            ترقية <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}
      {p.level === "ok" && (
        <div className="mt-4 pt-3 border-t border-foreground/5 text-xs text-emerald-700 flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5" /> الاستهلاك آمن — لا حاجة للترقية حالياً
        </div>
      )}
    </Card>
  );
}

const EVENT_LABEL = {
  download: "تنزيل",
  upload: "رفع",
  extract_from_file: "استخراج من ملف",
  extract_recipe_video: "استخراج من فيديو",
  generate_recipes: "توليد وصفات",
  generate_image: "توليد صورة",
  objects: "عدد الملفات",
  uploads_this_month: "رفع هذا الشهر",
  bytes_this_month: "بايت هذا الشهر",
  video_count: "عدد الفيديوهات",
  total_seconds: "إجمالي الثواني",
  total_bytes: "إجمالي البايت",
};
